/**
 * DependsOnValidationDialog Component
 * 
 * Displays detailed information about fields that have values but are hidden or disabled,
 * preventing form submission until the issue is resolved.
 * 
 * Features:
 * - Shows a list of problematic fields with their labels and current values
 * - Explains why each field is problematic (hidden vs disabled)
 * - Provides "Review" button to close the dialog and manually fix issues
 * - Provides "Clear Values and Save" button to automatically clear problematic fields and save
 * 
 * @example
 * <DependsOnValidationDialog
 *   open={dialogOpen}
 *   problematicFields={validationResult.problematicFields}
 *   onClose={handleClose}
 *   onClearAndSave={handleClearAndSave}
 * />
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  useTheme,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BlockIcon from '@mui/icons-material/Block';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CustomButton from '../@extended/CustomButton';

/**
 * Format field value for display
 * @param {*} value - The field value
 * @returns {string} - Formatted value string
 */
const formatFieldValue = (value) => {
  if (value === null || value === undefined) {
    return 'null';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '(empty)';
    }
    return value.join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  if (typeof value === 'string' && value.length > 50) {
    return value.substring(0, 50) + '...';
  }
  return String(value);
};

/**
 * Get clearing instruction based on field type
 * @param {string} fieldType - The type of the field
 * @param {string} subtype - Optional subtype (e.g., 'DATE' for INLINE_TEXT_FIELD)
 * @returns {string} - Human-readable instruction on how to clear the field
 */
const getClearingInstruction = (fieldType, subtype) => {
  // Handle date fields (can be INLINE_TEXT_FIELD with DATE subtype or standalone)
  if (subtype === 'DATE' || fieldType === 'DATE_PICKER') {
    return 'Click on the field to open the date picker, then click the "Clear" button.';
  }

  switch (fieldType) {
    case 'RADIO_BUTTONS':
      return 'Click on the currently selected option again to deselect it.';
    
    case 'DROP_DOWN':
    case 'SELECT':
      return 'Open the dropdown and select an empty option, or click the clear (×) icon if available.';
    
    case 'MULTISELECT_DROP_DOWN':
    case 'SEARCH_DROP_DOWN':
      return 'Click the (×) icon next to each selected value to remove it, or click the clear all (×) icon.';
    
    case 'CHECK_BOX':
      return 'Uncheck the checkbox by clicking on it.';
    
    case 'CHECK_BOX_GROUP':
      return 'Uncheck all selected checkboxes by clicking on each one.';
    
    case 'INLINE_TEXT_FIELD':
    case 'TEXT_FIELD':
    case 'TEXT_AREA':
      return 'Select all text (Ctrl+A / Cmd+A) and delete it, or use the clear (×) icon if available.';
    
    case 'YEAR_PICKER':
      return 'Click on the field and clear the year value, or use the clear button if available.';
    
    case 'SLIDER':
      return 'Drag the slider to its minimum/default position.';
    
    case 'SIGNATURE':
      return 'Click the "Clear" button below the signature pad to remove the signature.';
    
    case 'FILE_UPLOAD':
      return 'Click the delete/remove icon next to the uploaded file to remove it.';
    
    case 'PHONE_INPUT':
      return 'Clear the phone number by selecting all text and deleting it.';
    
    case 'AUDIO':
      return 'Click the delete/remove button to remove the recorded audio.';
    
    case 'NUMBER':
      return 'Clear the number by selecting the text and deleting it, or set it to 0.';
    
    default:
      return 'Clear the field value by selecting and deleting the content, or use the clear button if available.';
  }
};

/**
 * Get the icon for the constraint type
 * @param {string} constraintType - 'visibility' or 'disabled'
 * @returns {React.ReactNode} - Icon component
 */
const getConstraintIcon = (constraintType) => {
  switch (constraintType) {
    case 'visibility':
      return <VisibilityOffIcon color="warning" />;
    case 'disabled':
      return <BlockIcon color="error" />;
    default:
      return <ErrorOutlineIcon color="error" />;
  }
};

/**
 * Get human-readable constraint type label
 * @param {string} constraintType - 'visibility' or 'disabled'
 * @returns {string} - Human-readable label
 */
const getConstraintLabel = (constraintType) => {
  switch (constraintType) {
    case 'visibility':
      return 'Hidden Field';
    case 'disabled':
      return 'Disabled Field';
    default:
      return 'Invalid Field';
  }
};

/**
 * DependsOnValidationDialog Component
 */
const DependsOnValidationDialog = ({
  open,
  problematicFields = [],
  onClose,
  onClearAndSave,
  title = 'Form Validation Issue',
  reviewButtonLabel = 'Review',
  clearAndSaveButtonLabel = 'Clear Values and Save',
}) => {
  const theme = useTheme();

  if (!problematicFields || problematicFields.length === 0) {
    return null;
  }

  const handleClearAndSave = () => {
    if (onClearAndSave) {
      onClearAndSave(problematicFields);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="depends-on-validation-dialog-title"
      aria-describedby="depends-on-validation-dialog-description"
    >
      <DialogTitle id="depends-on-validation-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6" component="span">
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography id="depends-on-validation-dialog-description" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The following fields contain values but are currently hidden or disabled based on your form selections. Please
          review and either clear these values or adjust your selections.
        </Typography>

        <List sx={{ bgcolor: theme.palette.background.default, borderRadius: 1 }}>
          {problematicFields.map((field, index) => (
            <React.Fragment key={field.fieldPath}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                alignItems="flex-start"
                sx={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  py: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>{getConstraintIcon(field.constraintType)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" component="span" fontWeight="bold">
                          Field:
                        </Typography>
                        <Typography variant="subtitle2" component="span" fontWeight="medium">
                          {field.fieldLabel}
                        </Typography>
                        <Chip
                          label={getConstraintLabel(field.constraintType)}
                          size="small"
                          color={field.constraintType === 'visibility' ? 'warning' : 'error'}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      field.categoryLabel && (
                        <Typography variant="caption" color="text.secondary">
                          Section: {field.categoryLabel}
                        </Typography>
                      )
                    }
                  />
                </Box>

                <Box sx={{ ml: 5, mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <strong>Current Value:</strong>{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        bgcolor: theme.palette.action.hover,
                        px: 0.5,
                        borderRadius: 0.5,
                      }}
                    >
                      {formatFieldValue(field.currentValue)}
                    </Typography>
                  </Typography>

                  {/* Show how to clear this field type */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <strong>How to clear:</strong>{' '}
                    <Typography component="span" variant="body2" color="primary.main">
                      {getClearingInstruction(field.fieldType, field.subtype)}
                    </Typography>
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {field.reason}
                  </Typography>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
        </List>

        <Box
          sx={{
            mt: 2,
            p: 1.5,
            bgcolor: theme.palette.info.lighter || theme.palette.action.hover,
            borderRadius: 1,
            border: `1px solid ${theme.palette.info.light}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Options:</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            • <strong>{reviewButtonLabel}:</strong> Close this dialog and show error messages on the affected fields.
            The form will scroll to the first problematic field.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            • <strong>{clearAndSaveButtonLabel}:</strong> Automatically clear the values from all listed fields above
            and proceed with saving the form.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
        <CustomButton variant="outlined" onClick={onClose} color="primary">
          {reviewButtonLabel}
        </CustomButton>
        <CustomButton variant="contained" onClick={handleClearAndSave} color="primary">
          {clearAndSaveButtonLabel}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

DependsOnValidationDialog.propTypes = {
  /** Whether the dialog is open */
  open: PropTypes.bool.isRequired,
  /** Array of problematic field objects from validation service */
  problematicFields: PropTypes.arrayOf(
    PropTypes.shape({
      fieldKey: PropTypes.string.isRequired,
      fieldLabel: PropTypes.string.isRequired,
      fieldPath: PropTypes.string.isRequired,
      categoryKey: PropTypes.string,
      categoryLabel: PropTypes.string,
      currentValue: PropTypes.any,
      constraintType: PropTypes.oneOf(['visibility', 'disabled']).isRequired,
      reason: PropTypes.string,
      controllingFields: PropTypes.array,
      fieldType: PropTypes.string,
      subtype: PropTypes.string,
    })
  ).isRequired,
  /** Callback when dialog is closed (Review button) */
  onClose: PropTypes.func.isRequired,
  /** Callback when user wants to clear values and save */
  onClearAndSave: PropTypes.func.isRequired,
  /** Custom dialog title */
  title: PropTypes.string,
  /** Custom label for the review button */
  reviewButtonLabel: PropTypes.string,
  /** Custom label for the clear and save button */
  clearAndSaveButtonLabel: PropTypes.string,
};

export default DependsOnValidationDialog;

